import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Button, ClickAwayListener, Popper, Typography } from "@mui/material";
import type { AutocompleteCloseReason } from "@mui/material/useAutocomplete";
import { type FC, type SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Department } from "../../contexts/AuthProvider";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { SURFACE } from "../../theme/tokens";
import { compareDepartments } from "../../utils/departmentOrder";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { headerStyles } from "./utils";

const POPPER_WIDTH = { xs: "100%", sm: 400 } as const;
const LISTBOX_MAX_HEIGHT = 480;

type Props = {
	departments: Department[];
	onChange: (department: Department) => void | Promise<void>;
	isBedSide?: boolean;
};
export const DepartmentSwitcher: FC<Props> = ({ departments, onChange, isBedSide = false }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();

	const [open, setOpen] = useState(false);
	const anchorRef = useRef<HTMLButtonElement>(null);
	const popperContentRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) {
			return;
		}

		const frameID = window.requestAnimationFrame(() => {
			popperContentRef.current?.querySelector<HTMLInputElement>("input")?.focus();
		});
		return () => {
			window.cancelAnimationFrame(frameID);
		};
	}, [open]);

	useEffect(() => {
		if (!open) {
			return;
		}

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				event.preventDefault();
				event.stopPropagation();
				setOpen(false);
			}
		};
		window.addEventListener("keydown", onKeyDown, true);
		return () => {
			window.removeEventListener("keydown", onKeyDown, true);
		};
	}, [open]);

	const hasMultiple = departments.length > 1;
	const options = useMemo<SystemAutoCompleteOption<Department>[]>(() => {
		return departments
			.slice()
			.sort(compareDepartments)
			.map((d) => ({ label: d.display_name, value: d }));
	}, [departments]);
	const selectedValue = useMemo<SystemAutoCompleteOption<Department> | undefined>(() => {
		return options.find((opt) => opt.value.department_id === chosenDepartment.department_id);
	}, [options, chosenDepartment.department_id]);
	const distinctHospitalCount = useMemo<number>(() => {
		return new Set(departments.map((d) => d.hospital)).size;
	}, [departments]);
	const groupBy = distinctHospitalCount > 1 ? (opt: SystemAutoCompleteOption<Department>) => opt.value.hospital : undefined;

	const filterOptions = useCallback((opts: SystemAutoCompleteOption<Department>[], state: { inputValue: string }): SystemAutoCompleteOption<Department>[] => {
		const term = state.inputValue.trim().toLowerCase();
		if (!term) {
			return opts;
		}

		return opts.filter(({ value: dept }) => {
			return dept.display_name.toLowerCase().includes(term) || dept.department_id.toLowerCase().includes(term) || dept.hospital.toLowerCase().includes(term);
		});
	}, []);

	const handleTriggerClick = useCallback((): void => {
		if (!hasMultiple) {
			return;
		}
		setOpen((prev) => !prev);
	}, [hasMultiple]);

	const handleClose = useCallback((): void => {
		setOpen(false);
	}, []);

	const handleChange = useCallback(
		async (_event: SyntheticEvent, opt: SystemAutoCompleteOption<Department> | null): Promise<void> => {
			if (!opt) {
				return;
			}

			try {
				await onChange(opt.value);
			} catch (e) {
				console.error(e);
			}
			setOpen(false);
		},
		[onChange],
	);

	const handleOpenStateChange = useCallback(
		(_event: SyntheticEvent, reason: AutocompleteCloseReason | undefined, isOpen: boolean): void => {
			if (!isOpen && reason === "selectOption") handleClose();
		},
		[handleClose],
	);
	const triggerSx = isBedSide ? headerStyles.departmentButtonTextBedSide : headerStyles.departmentButtonText;

	// No `data-annie-target` on this branch: it is a label, not a switcher, and the walkthrough step on
	// that anchor says "switch between them". Dropping the anchor is the supported way to drop a step for
	// the users it doesn't apply to — WalkthroughController runs only the steps whose anchors are visible.
	if (!hasMultiple) {
		return (
			<Box data-testid="header-open-departments" sx={headerStyles.departmentButton}>
				<Typography sx={triggerSx}>{chosenDepartment.display_name || t("Department")}</Typography>
			</Box>
		);
	}

	return (
		<Box>
			<Button
				ref={anchorRef}
				data-testid={"header-open-departments"}
				// Only the branch that can actually be clicked carries the anchor.
				data-annie-target="department-switcher"
				color="inherit"
				onClick={handleTriggerClick}
				endIcon={<KeyboardArrowDownIcon />}
				sx={headerStyles.departmentButton}
				aria-haspopup="listbox"
				aria-expanded={open}
			>
				<Typography sx={triggerSx}>{chosenDepartment.display_name || t("Department")}</Typography>
			</Button>
			<Popper open={open} anchorEl={anchorRef.current} placement="bottom-end" sx={{ zIndex: (theme) => theme.zIndex.modal, width: POPPER_WIDTH }}>
				<ClickAwayListener onClickAway={handleClose}>
					<Box ref={popperContentRef} sx={{ backgroundColor: SURFACE, borderRadius: 1, boxShadow: 3, overflow: "hidden", width: "100%" }}>
						<SystemAutoComplete<Department>
							open
							options={options}
							value={selectedValue}
							onChange={handleChange}
							onOpenStateChange={handleOpenStateChange}
							filterOptions={filterOptions}
							groupBy={groupBy}
							fullWidth
							dataTestId="header-departments-items"
							getOptionDataTestId={({ value }) => `department-item-${value.department_id}`}
							textFieldProps={{
								placeholder: t("search.department"),
								reserveHeightForHelperText: false,
								formControlSx: { paddingBottom: "5px", padding: 1 },
							}}
							slotProps={{
								popupIndicator: {
									sx: { display: "none" },
								},
								popper: {
									disablePortal: true,
									sx: { width: "100% !important", position: "static !important", transform: "none !important" },
								},
								paper: {
									sx: { boxShadow: "none", borderRadius: 0, mt: 0 },
								},
								listbox: {
									sx: {
										maxHeight: LISTBOX_MAX_HEIGHT,
										"& .MuiAutocomplete-option": { paddingX: 1 },
										"& .MuiAutocomplete-groupLabel": { fontWeight: "bold", paddingY: 0 },
									},
								},
							}}
						/>
					</Box>
				</ClickAwayListener>
			</Popper>
		</Box>
	);
};
